/**
 * B.42 全局 Toast —— 非阻塞浮层通知,替代单 op 场景的 window.alert。
 *
 * 用法:
 *   import { useToast } from '@/composables/useToast'
 *   const toast = useToast()
 *   toast.success('已新建相册')
 *   toast.error('上传失败:网络超时')   // 默认 stay 5s,更显眼
 *   toast.info('已跳过 3 个超大文件')
 *
 * 实现:模块级 reactive 单实例 + 容器组件 (ToastContainer.vue) 在 AppLayout
 * 挂一次。useToast() 返回 ref 的 items 给容器渲染 + push/dismiss API 给调用方。
 *
 * 跟 BatchResultModal 的分工:
 *   - BatchResultModal —— 批量操作(N 项,有局部成功 + 局部失败),用户要看完整列表
 *   - Toast            —— 单 op,只显一行结果文本,3-5s 自动消失
 *
 * 跟 window.alert 的差别:
 *   - 非阻塞 (用户能继续操作)
 *   - 多条堆叠不互相覆盖
 *   - 自动 dismiss + 手动 × 关
 *   - 视觉跟应用主题一致 (CSS variable)
 */
import { reactive } from 'vue'

export type ToastKind = 'success' | 'error' | 'info'

export interface ToastItem {
  id: number
  kind: ToastKind
  message: string
}

interface ToastState {
  items: ToastItem[]
}

const state: ToastState = reactive({ items: [] })

let counter = 0

function push(kind: ToastKind, message: string, ttlMs: number) {
  const id = ++counter
  state.items.push({ id, kind, message })
  if (ttlMs > 0) {
    setTimeout(() => dismiss(id), ttlMs)
  }
  return id
}

function dismiss(id: number) {
  const idx = state.items.findIndex((t) => t.id === id)
  if (idx >= 0) state.items.splice(idx, 1)
}

export function useToast() {
  return {
    /** 内部 reactive items,容器组件用 */
    items: state.items,
    /** 成功 toast,默认 2.5s */
    success: (message: string, ttlMs = 2500) => push('success', message, ttlMs),
    /** 错误 toast,默认 4s(用户需要更多时间读)*/
    error: (message: string, ttlMs = 4000) => push('error', message, ttlMs),
    /** 中性 info,默认 2.5s */
    info: (message: string, ttlMs = 2500) => push('info', message, ttlMs),
    /** 手动 dismiss(× 按钮 / 测试用)*/
    dismiss
  }
}
